import React from 'react'
import Axios from 'axios'
import ReactSelect from 'components/common/ReactSelect'
import { API } from 'api/APIClient'
import AppComponent from 'components/common/AppComponent'
import ModalProgress from 'components/common/ModalProgress'
import moment from 'moment'
import DateHelper from 'utils/DateHelper'

export default class NotifProvidersForm extends AppComponent {

    static defaultProps = {
        item: {}
    }

    constructor(props, context) {
        super(props);

        this.state = {
            item: Object.assign({
                createdAt: moment.utc().format("YYYY-MM-DD HH:mm:ss"),
                organisationID: context.organisation.id,
                providerID: "",
                values: {},
                properties: [],
                name: ""
            }, props.item),
            organisationOptions: [
                props.item.organisationID ? {value: props.item.organisationID, label: props.item.organisationName} : {value: context.organisation.id, label: context.organisation.name}
            ],
            providerOptions: [],
            validated: false,
            errors: {},
            touched: {},
            isUploading: false
        };
    }

    componentDidMount() {
        this.validate();
        if (this.context.permissions.notificationProviders.allOrgs) {
            this.getOrganisationOptions();
        }
        this.getProviderOptions();
    }

    getProviderOptions = () => {
        Axios
            .get(API.notificationProvider.getProviders)
            .then((res) => {
                if (res.data.success) {
                    this.setState({
                        providerOptions: res.data.data.providers.map((item) => {
                            return {
                                value: item.id.toString(),
                                label: item.name,
                                properties: item.properties || []
                            }
                        })
                    })
                }
            })
    }

    getOrganisationOptions = () => {
        Axios
            .get(API.organisation.getOrganisations, {
                params: {
                    appUsersOnly: 1
                }
            })
            .then((res) => {
                if (res.data.success) {
                    this.setState({
                        organisationOptions: res.data.data.organisations.map((item) => { return {value: item.id.toString(), label: item.name} })
                    })
                }
            })
    }

    handleSubmit = (event) => {
        event.preventDefault();
        if (! this.state.isUploading) {
            this.setState({
                isUploading: true
            }, () => {
                const submitData = {
                    organisationID: this.state.item.organisationID,
                    providerID: this.state.item.providerID,
                    values: this.state.item.values,
                    name: this.state.item.name
                };

                if (this.state.item.id) {
                    submitData.id = this.state.item.id;
                }

                Axios
                    .post((this.state.item.id ? API.notificationProvider.updateConfig : API.notificationProvider.createConfig), submitData)
                    .then((res) => {
                        if (res.data.success) {
                            this.props.onSubmitted(res.data.data.config);
                        }
                        else if (res.data.error) {
                            let errors = this.state.errors;
                            errors.genericError = res.data.error.desc;
                            this.setState({
                                errors: errors
                            });
                        }

                        this.setState({
                            isUploading: false
                        });
                    })
                    .catch((error) => {
                        let errors = this.state.errors;
                        errors.genericError = "An error occurred while saving";
                        this.setState({
                            errors: errors,
                            isUploading: false
                        });
                    })
            })
        }
    }

    handleBlur = (event) => {
        const target = event.target;
        const name = target.name;

        let touched = this.state.touched;
        touched[name] = true;

        this.setState({
            touched: touched
        }, () => {
            this.validate()
        })
    }

    handleChange = (event) => {
        const target = event.target;
        const value = target.type === 'checkbox' ? (target.checked ? 1 : 0) : target.value;
        const name = target.name;

        if (name === "providerID") {
            const provider = this.state.providerOptions.find(p => p.value === value);
            if (provider) {
                this.setState({
                    item: {
                        ...this.state.item,
                        providerID: value,
                        values: {},                          // New empty object
                        properties: [...provider.properties] // Copy the array
                    }
                }, () => {
                    this.validate()
                });
            }
        } else {
            this.setState({
                item: {
                    ...this.state.item,
                    [name]: value  // Dynamic property update
                }
            }, () => {
                this.validate()
            });
        }
    }

    handleDynamicFieldChange = (event) => {
        const target = event.target;
        const value = target.type === 'number'
            ? (target.value ? parseFloat(target.value) : '')
            : target.value;
        const name = target.name;

        this.setState({
            item: {
                ...this.state.item,
                values: {
                    ...this.state.item.values,
                    [name]: value
                }
            }
        }, () => {
            this.validate()
        });
    }

    validate = () => {
        let validated = true;
        let errors = {};

        const item = this.state.item;
        const touched = this.state.touched;
        const isEditMode = !!item.id;

        // For new records, require organisation and provider
        if (!item.organisationID) {
            if (touched.organisationID || isEditMode) {
                errors.organisationID = "Organisation is required";
            }
            if (!isEditMode) {
                validated = false;
            }
        }

        if (!item.providerID) {
            if (touched.providerID || isEditMode) {
                errors.providerID = "Provider is required";
            }
            if (!isEditMode) {
                validated = false;
            }
        }

        // Validate dynamic fields
        if (item.properties && item.properties.length > 0) {
            item.properties.forEach((property) => {
                const fieldValue = item.values[property.key];
                // Check if value is empty (but allow 0 for numbers)
                if (fieldValue === '' || fieldValue === null || fieldValue === undefined) {
                    if (touched[property.key] || isEditMode) {
                        errors[property.key] = `${property.name} is required`;
                    }
                    validated = false;
                }
            });
        }

        this.setState({
            validated: validated,
            errors: errors
        });
    }

    renderDynamicFields = () => {
        const { item } = this.state;

        if (!item.properties || item.properties.length === 0) {
            return null;
        }

        return item.properties.map((property) => {
            const value = item.values[property.key] || '';
            let inputElement;

            if (property.type === 'option' && property.options) {
                // Render select dropdown for option type
                inputElement = (
                    <select
                        name={property.key}
                        onChange={this.handleDynamicFieldChange}
                        value={value}
                        onBlur={this.handleBlur}
                    >
                        <option value="">-- Select {property.name} --</option>
                        {property.options.map((option) => (
                            <option key={option.value} value={option.value}>
                                {option.name}
                            </option>
                        ))}
                    </select>
                );
            } else {
                // Render input field for string/int types
                const inputType = property.type === 'int' ? 'number' : 'text';
                inputElement = (
                    <input
                        type={inputType}
                        name={property.key}
                        onChange={this.handleDynamicFieldChange}
                        value={value}
                        onBlur={this.handleBlur}
                    />
                );
            }

            return (
                <div className="grid-x grid-padding-x" key={property.key}>
                    <div className="small-3 cell">
                        <label htmlFor={property.key} className="text-right middle">* {property.name}</label>
                    </div>
                    <div className={"small-9 cell " + (this.state.errors[property.key] && "error")}>
                        {inputElement}
                        <small className="error">{this.state.errors[property.key]}</small>
                    </div>
                </div>
            );
        });
    }

    render() {
        return (
            <React.Fragment>
                <ModalProgress show={this.state.isUploading} />
                <div className="modal-head">
                    <h3>{this.state.item.id ? "Edit Notifications Provider Config" : "New Notifications Provider Config"}</h3>
                </div>
                <form onSubmit={this.handleSubmit}>
                    <div className="modal-body">
                        <div className="grid-x grid-padding-x">
                            <div className="small-3 cell">
                                <label htmlFor="createdAt" className="text-right middle">Created At</label>
                            </div>
                            <div className="large-9 small-9 cell">
                                <input
                                    name="createdAt"
                                    type="text"
                                    value={moment(DateHelper.convertToLocalDateTime(this.state.item.createdAt), "YYYY-MM-DD HH:mm:ss").format("LLLL")}
                                    disabled={true}
                                />
                            </div>
                        </div>

                        <div className="grid-x grid-padding-x">
                            <div className="small-3 cell">
                                <label htmlFor="name" className="text-right middle ">* Name</label>
                            </div>
                            <div className={"small-9 cell " + (this.state.errors.name && "error")}>
                                <input type="text" name="name" onChange={this.handleChange} value={this.state.item.name} onBlur={this.handleBlur} />
                                <small className="error">{this.state.errors.name}</small>
                            </div>
                        </div>
                        <div className="form-error-msg grid-x grid-padding-x" style={{display: this.state.errors.genericError ? "block" : "none"}}>
                            <div className="cell small-12">
                                <span>{this.state.errors.genericError}</span>
                            </div>
                        </div>

                        {this.context.permissions.notificationProviders.allOrgs && !this.props.item.id ?
                            <div className="grid-x grid-padding-x">
                                <div className="small-3 cell">
                                    <label htmlFor="organisationID" className="text-right middle">* Organisation</label>
                                </div>
                                <div className={"small-9 cell " + (this.state.errors.organisationID && "error")}>
                                    <ReactSelect
                                        name="organisationID"
                                        className="react-select"
                                        onChange={this.handleChange}
                                        value={this.state.item.organisationID}
                                        options={this.state.organisationOptions}
                                        onBlur={this.handleBlur}
                                    />
                                    <small className="error">{this.state.errors.organisationID}</small>
                                </div>
                            </div>
                            :
                            <div className="grid-x grid-padding-x">
                                <div className="small-3 cell">
                                    <label htmlFor="organisationName" className="text-right middle">* Organisation</label>
                                </div>
                                <div className="small-9 cell">
                                    <input
                                        type="text"
                                        name="organisationName"
                                        value={this.state.organisationOptions.find(o => o.value == this.state.item.organisationID)?.label || ''}
                                        disabled
                                    />
                                </div>
                            </div>
                        }

                        {!this.props.item.id ?
                            <div className="grid-x grid-padding-x">
                                <div className="small-3 cell">
                                    <label htmlFor="providerID" className="text-right middle">* Provider</label>
                                </div>
                                <div className={"small-9 cell " + (this.state.errors.providerID && "error")}>
                                    <ReactSelect
                                        name="providerID"
                                        className="react-select"
                                        onChange={this.handleChange}
                                        value={this.state.item.providerID}
                                        options={this.state.providerOptions}
                                        onBlur={this.handleBlur}
                                    />
                                    <small className="error">{this.state.errors.providerID}</small>
                                </div>
                            </div>
                            :
                            <div className="grid-x grid-padding-x">
                                <div className="small-3 cell">
                                    <label htmlFor="providerName" className="text-right middle">* Provider</label>
                                </div>
                                <div className="small-9 cell">
                                    <input
                                        type="text"
                                        name="providerName"
                                        value={this.state.providerOptions.find(p => p.value == this.state.item.providerID)?.label || this.props.item.providerName || ''}
                                        disabled
                                    />
                                </div>
                            </div>
                        }

                        {this.renderDynamicFields()}

                        <div className="form-error-msg grid-x grid-padding-x" style={{display: this.state.errors.genericError ? "block" : "none"}}>
                            <div className="cell small-12">
                                <span>{this.state.errors.genericError}</span>
                            </div>
                        </div>
                    </div>
                    <div className="modal-footer">
                        <div className="grid-x grid-padding-x">
                            <div className="small-12 cell">
                                <div className="btn-wrap">
                                    <button className="button secondary" type="button" onClick={this.props.onCancel}>
                                        Cancel
                                    </button>
                                    <button className="button success" type="submit" disabled={!this.state.validated}>
                                        Submit
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </form>
            </React.Fragment>
        );
    }
}